import { useEffect, useState } from "react";
import api, { formatApiError, resizeImageToBase64 } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, ImagePlus, X, AlertTriangle, Eye } from "lucide-react";

const STATUS_LABELS = {
  "non-lu": "Non lu",
  "en-attente": "En attente",
  "rapporte": "Rapporté",
  "regle": "Réglé",
};

export function ReportProblemModal({ open, onOpenChange, maintenance }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState([]);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    if (open && maintenance?.park_id) {
      api.get(`/problems/park/${maintenance.park_id}/unsolved`)
        .then(({ data }) => setExisting(data))
        .catch(() => setExisting([]));
    } else {
      setExisting([]);
    }
  }, [open, maintenance]);

  const reset = () => {
    setTitle("");
    setDescription("");
    setPhotos([]);
  };

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    const b64 = await Promise.all(files.map((f) => resizeImageToBase64(f)));
    setPhotos((p) => [...p, ...b64]);
    e.target.value = "";
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Le titre est requis.");
    setLoading(true);
    try {
      await api.post("/problems", {
        title,
        description,
        photos,
        park_id: maintenance?.park_id || null,
      });
      toast.success("Problème rapporté. Statut : non-lu.");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="report-problem-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Rapporter un problème
          </DialogTitle>
        </DialogHeader>
        {maintenance?.park_name && (
          <p className="text-sm text-muted-foreground -mt-2">Parc : {maintenance.park_name}</p>
        )}

        {existing.length > 0 && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="existing-problems-list">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">
              Problèmes déjà signalés pour ce parc — vérifiez avant d'en créer un
            </p>
            <ul className="space-y-1 max-h-32 overflow-y-auto">
              {existing.map((p) => (
                <li key={p.id}>
                  <button
                    data-testid={`existing-problem-${p.id}`}
                    onClick={() => setViewer(p)}
                    className="flex items-center gap-2 text-sm text-left font-medium text-amber-800 dark:text-amber-300 hover:underline"
                  >
                    <Eye className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{p.title}</span>
                    <Badge variant="secondary" className="text-[10px] shrink-0">{STATUS_LABELS[p.status]}</Badge>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pt">Titre</Label>
            <Input id="pt" data-testid="problem-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex : Clôture endommagée" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pd">Description</Label>
            <Textarea id="pd" data-testid="problem-description-input" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Décrivez le problème découvert..." />
          </div>
          <div className="space-y-2">
            <Label>Photos</Label>
            <div className="flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative h-20 w-20 rounded-lg overflow-hidden border border-border">
                  <img src={p} alt="" className="h-full w-full object-cover" />
                  <button
                    onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                    className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label
                data-testid="problem-add-photo-btn"
                className="h-20 w-20 grid place-items-center rounded-lg border-2 border-dashed border-border cursor-pointer hover:border-primary transition-colors text-muted-foreground"
              >
                <ImagePlus className="h-5 w-5" />
                <input type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={addPhotos} />
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button data-testid="problem-submit-btn" onClick={submit} disabled={loading} className="active:scale-95 transition-transform">
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="existing-problem-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {viewer?.title}
              {viewer && <Badge variant="secondary">{STATUS_LABELS[viewer.status]}</Badge>}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{viewer?.description || "Aucune description."}</p>
          {viewer?.reported_by_name && (
            <p className="text-xs text-muted-foreground">Signalé par : {viewer.reported_by_name}</p>
          )}
          {viewer?.photos?.length > 0 && (
            <div className="grid grid-cols-2 gap-2 max-h-[50vh] overflow-y-auto">
              {viewer.photos.map((ph, i) => (
                <img key={i} src={ph} alt="" className="rounded-lg border border-border w-full object-cover" />
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground italic">Lecture seule</p>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
